import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { normalizeTelegramPayload } from '@/components/meetingsData';

/*
  MeetingsContext — per-project meetings, backed by the real API.
  Meetings live on the project document (projects.meetings), so records created
  from the software AND from the Telegram bot both surface here. The Design Fee
  tab reads the same store, so on-site meetings stay in sync.
  Mount with key={projectId} so each project loads its own meetings.
*/

const MeetingsContext = createContext(null);

const KNOWN_LOCATIONS = ['on_site', 'office', 'selection', 'other'];
const toArr = (x) => (Array.isArray(x) ? x : x ? [String(x)] : []);
const normLoc = (l) => {
  if (!l) return 'on_site';
  const s = String(l).toLowerCase().replace(/[\s-]+/g, '_');
  if (['on_site', 'onsite', 'site', 'on_site_visit', 'sitevisit'].includes(s)) return 'on_site';
  if (KNOWN_LOCATIONS.includes(s)) return s;
  return 'other';
};
const normalizeMeeting = (m) => {
  const loc = normLoc(m.location);
  return {
    ...m,
    decisions: toArr(m.decisions),
    actions: toArr(m.actions),
    location: loc,
    // preserve original label for display when we had to bucket into "other"
    locationCustom: m.locationCustom || (loc === 'other' && m.location && !KNOWN_LOCATIONS.includes(String(m.location)) ? String(m.location) : (m.locationCustom || '')),
  };
};

export function MeetingsProvider({ children, projectId }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!projectId) { setLoading(false); return; }
    try {
      const res = await api.get(`/projects/${projectId}`);
      setMeetings((res.data.meetings || []).map(normalizeMeeting));
    } catch {
      /* leave empty on error */
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { refresh(); }, [refresh]);

  // POST /projects/:id/meetings  |  PUT /projects/:id/meetings/:mid
  const saveMeeting = async (meetingData) => {
    if (meetingData.id && meetings.some(m => m.id === meetingData.id)) {
      const res = await api.put(`/projects/${projectId}/meetings/${meetingData.id}`, meetingData);
      const nm = normalizeMeeting(res.data);
      setMeetings(prev => prev.map(m => (m.id === meetingData.id ? { ...m, ...nm } : m)));
      return nm;
    }
    const res = await api.post(`/projects/${projectId}/meetings`, meetingData);
    const nm = normalizeMeeting(res.data);
    setMeetings(prev => [...prev, nm]);
    return nm;
  };

  // DELETE /projects/:id/meetings/:mid
  const deleteMeeting = async (meetingId) => {
    await api.delete(`/projects/${projectId}/meetings/${meetingId}`);
    setMeetings(prev => prev.filter(m => m.id !== meetingId));
  };

  // Telegram-created meetings already persist via the bot webhook; this stays as
  // a client-side integration point and simply re-syncs from the backend.
  const onTelegramMeetingReceived = async (parsedPayload) => {
    if (parsedPayload) await saveMeeting(normalizeTelegramPayload(parsedPayload));
    else await refresh();
  };

  return (
    <MeetingsContext.Provider value={{ meetings, loading, saveMeeting, deleteMeeting, refresh, onTelegramMeetingReceived }}>
      {children}
    </MeetingsContext.Provider>
  );
}

export function useMeetings() {
  const ctx = useContext(MeetingsContext);
  if (!ctx) throw new Error('useMeetings must be used within a MeetingsProvider');
  return ctx;
}
